/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { DEFAULT_CURRENCY_TEMPLATE } from '@gh/config';
import { get, isNil } from 'lodash';

export function substitute(str: string, params: Hash<string>): string {
  return str.replace(/{([a-zA-Z0-9_.]+)}/g, (_, path) => {
    const text = get(params, path);

    return isNil(text) ? '' : text;
  });
}

export function inDefaultCurrency(price: string): string {
  return substitute(DEFAULT_CURRENCY_TEMPLATE, splitLikeNumber(price));
}

/**
 * Transforms given string to allow it used as plain string in regular expression
 */
export function regexpablePlainString(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\-]/g, '\\$&');
}

const NUMBER_RE = /^([-]?)(.+)$/;

/**
 * Splits localized number string on sign and absolute number
 */
export function splitLikeNumber(str: string): { sign: '-' | ''; number: string } {
  const match = NUMBER_RE.exec(str);
  return match ? { sign: <any>match[1], number: match[2] } : { sign: '', number: '0' };
}

export function includesIgnoreCase(str: string, sub: string): boolean {
  const loweredSub = sub ? sub.toLowerCase() : void 0;
  return loweredSub ? str.toLowerCase().includes(loweredSub) : true;
}

/**
 * Normalizes text by removing HTML tags, extra whitespace, and decoding HTML entities.
 */
export function normalizeText(text: string): string {
  return text
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\n+/g, ' ')
    .replace(/&amp;/g, '&')
    .trim();
}

export function normalizeForXeroTrackingInput(value: string | null | undefined): string {
  if (value == null) {
    return '';
  }
  return value.replace(/\s+/gu, ' ').normalize('NFC');
}

const XERO_TRACKING_EXTRA_ASCII_MAP: Readonly<Record<string, string>> = {
  '\u00D0': 'D', // Ð
  '\u00D8': 'O', // Ø
  '\u00F0': 'd', // ð
  '\u00F8': 'o', // ø
  '\u0110': 'D', // Đ
  '\u0111': 'd', // đ
  '\u0141': 'L', // Ł
  '\u0142': 'l', // ł
  '\u00E6': 'ae', // æ
  '\u00C6': 'AE', // Æ
  '\u0153': 'oe', // œ
  '\u0152': 'OE', // Œ
  '\u00DF': 'ss', // ß
  '\u1E9E': 'SS', // ẞ (capital sharp s)
  '\u00FE': 'th', // þ
  '\u00DE': 'TH', // Þ
};

function applyXeroTrackingExtraAsciiMap(s: string): string {
  let result = '';
  for (const ch of s) {
    result += XERO_TRACKING_EXTRA_ASCII_MAP[ch] ?? ch;
  }
  return result;
}

export function foldToAsciiForXeroTrackingInput(value: string | null | undefined): string {
  const base = normalizeForXeroTrackingInput(value);
  if (!base) {
    return '';
  }
  const stripped = base
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .normalize('NFC');
  return applyXeroTrackingExtraAsciiMap(stripped);
}

export function foldToAsciiForXeroTrackingInputTrimmed(value: string | null | undefined): string {
  return foldToAsciiForXeroTrackingInput(value).trim();
}
